import { useState } from 'react';
import { Alert, Box, Button, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { Company } from 'src/secret-menu/hooks/useCompanies';
import PageLoader from 'src/UI/loader/PageLoader';
import BotImageUpload from './components/BotImageUpload';
import BotNameConfig from './components/BotNameConfig';
import BrandingPackSection from './components/BrandingPackSection';
import ColorPaletteSection from './components/ColorPaletteSection';
import SectionHeader from './components/SectionHeader';
import {
  useDownloadBrandingPack,
  useExportBranding,
  useUpdateExportBranding,
  useUploadCompanyLogo,
  useUploadExportAssetsFile,
  useUploadTitlePage,
} from './hooks/useExportBranding';
import type { ExportBrandingConfigData } from './types';
import { DEFAULT_CONFIG } from './types';

type Props = {
  company: Company;
};

export default function ExportAssetsContent({ company }: Props) {
  const { branding, isLoading, isError, refetch } = useExportBranding(company.id);
  const { updateBranding, isLoading: isSaving } = useUpdateExportBranding(company.id);
  const { uploadFile: uploadExportAssets, isLoading: isUploadingExportAssets } =
    useUploadExportAssetsFile(company.id);
  const { uploadFile: uploadCompanyLogo, isLoading: isUploadingCompanyLogo } = useUploadCompanyLogo(
    company.id,
  );
  const { uploadFile: uploadTitlePage, isLoading: isUploadingTitlePage } = useUploadTitlePage(
    company.id,
  );
  const { downloadPack, isLoading: isDownloadingPack } = useDownloadBrandingPack(company.id);

  const [localConfig, setLocalConfig] = useState<ExportBrandingConfigData | null>(null);
  const [isResettingPalette, setIsResettingPalette] = useState(false);

  const config = localConfig ?? branding?.config ?? DEFAULT_CONFIG;

  const hasUnsavedChanges = localConfig !== null;

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError) {
    return (
      <Alert color="red" variant="light" title="Error">
        Failed to load branding configuration. Please try again.
      </Alert>
    );
  }

  const handleSave = async () => {
    try {
      await updateBranding(config);
      setLocalConfig(null);
      await refetch();
      notifications.show({
        title: 'Saved',
        message: 'Branding configuration saved.',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to save configuration.',
        color: 'red',
      });
    }
  };

  const handleChange = (updated: ExportBrandingConfigData) => {
    setLocalConfig(updated);
  };

  const handleResetPalette = async () => {
    setIsResettingPalette(true);
    try {
      const reset: ExportBrandingConfigData = {
        ...(branding?.config ?? DEFAULT_CONFIG),
        color_palette: DEFAULT_CONFIG.color_palette,
      };
      await updateBranding(reset);
      setLocalConfig(null);
      await refetch();
      notifications.show({
        title: 'Palette reset',
        message: 'Colour palette restored to brand defaults.',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to reset palette.',
        color: 'red',
      });
    } finally {
      setIsResettingPalette(false);
    }
  };

  return (
    <Stack gap="xxxl" maw={900} pb="xxxxl" p="md" bg="white">
      <Box pos="sticky" top={0} style={{ zIndex: 10 }} py="md" bg="white">
        <Group justify="space-between" align="center">
          <Box>
            <Text size="displayXs" fw={FONT_WEIGHTS.semibold}>
              Export branding
            </Text>
            <Text size="textMd" fw={FONT_WEIGHTS.medium} c="gray.6">
              {company.name} — {company.tenantName}
            </Text>
          </Box>
          <Button onClick={handleSave} loading={isSaving} disabled={!hasUnsavedChanges}>
            Save
          </Button>
        </Group>
      </Box>

      <ColorPaletteSection
        config={config}
        onChange={handleChange}
        onResetPalette={handleResetPalette}
        isResetting={isResettingPalette}
      />

      <Box>
        <SectionHeader
          title="Branding pack"
          subtitle={`Upload the branding files for this tenant. Files are saved to S3 under companies/${company.tenantName}/ and used directly for document exports.`}
        />
        <BrandingPackSection
          files={[
            {
              fileName: 'export_assets.docx',
              description:
                'The Word reference document defining the styles (headings, tables, bullets) applied to exported documents.',
              accept: '.docx',
              isDeployed: branding?.hasExportAssets ?? false,
              isUploading: isUploadingExportAssets,
              onUpload: uploadExportAssets,
            },
            {
              fileName: 'company_logo.png',
              description:
                'The company logo embedded in exported documents. Resized and stored as a transparent PNG.',
              accept: 'image/png,image/jpeg',
              isDeployed: branding?.hasCompanyLogo ?? false,
              isUploading: isUploadingCompanyLogo,
              onUpload: uploadCompanyLogo,
            },
            {
              fileName: 'title_page.docx',
              description: 'The title page template prepended to exported reports.',
              accept: '.docx',
              isDeployed: branding?.hasTitlePage ?? false,
              isUploading: isUploadingTitlePage,
              onUpload: uploadTitlePage,
            },
          ]}
          onDownloadPack={downloadPack}
          isDownloadingPack={isDownloadingPack}
        />
      </Box>

      <Box>
        <BotNameConfig companyId={company.id} />
      </Box>

      <Box>
        <BotImageUpload companyId={company.id} />
      </Box>
    </Stack>
  );
}
